import React, { useState, useEffect, useRef } from 'react';
import useIsMobile from '../../hooks/useIsMobile';
import { usePermissions } from '../../contexts/PermissionsContext';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import formationService from '../../services/formationService';
import { apiClient } from '../../services/authService';
import AddFormationDialog from '../../components/AddFormationDialog/AddFormationDialog';
import EditFormationDialog from '../../components/EditFormationDialog/EditFormationDialog';
import {
  Plus,
  BookOpen,
  Clock,
  Package,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  X,
  Archive,
  AlertTriangle
} from 'lucide-react';
import './FormationsPage.css';

const FormationsPage = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { confirm } = useConfirm();
  const [formations, setFormations] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isResponsable, isAdmin, user } = useAuth();
  const { canCreate, canEdit, canDelete, loading: permissionsLoading, permissions } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchTimeoutRef = useRef(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const allCentresLabel = i18n.exists('common.allCentres') ? t('common.allCentres') : 'Tous les centres';
  const [centres, setCentres] = useState([]);
  const availableCentres = isAdmin
    ? centres
    : (Array.isArray(user?.centres_details) ? user.centres_details : []);
  const hasMultipleCentres = availableCentres.length > 1;
  const [selectedCentre, setSelectedCentre] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [formationToEdit, setFormationToEdit] = useState(null);
  const isMobile = useIsMobile();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  const fetchFormations = async (page = 1, centreId = selectedCentre) => {
    try {
      setLoading(true);
      const params = {
        page: page,
        page_size: pageSize,
      };
      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }
      if (centreId) {
        params.centre = centreId;
      }

      if (filterStatus === 'active') {
        params.is_active = 'true';
      } else if (filterStatus === 'inactive') {
        params.is_active = 'false';
      } else if (isAdmin && showArchived) {
        params.include_archived = 'true';
      } else {
        params.is_active = 'true';
      }
      const response = await formationService.getAll(params);
      if (response.results) {
        setFormations(response.results);
        setTotalCount(response.count);
        setTotalPages(Math.ceil(response.count / pageSize));
      } else {
        setFormations(response || []);
        setTotalCount(response?.length || 0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching formations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFormations(currentPage, selectedCentre);
  }, [currentPage, debouncedSearchTerm, filterStatus, selectedCentre, showArchived]);

  useEffect(() => {
    const fetchCentres = async () => {
      if (!isAdmin) return;
      try {
        const res = await apiClient.get('/centres/');
        const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        setCentres(data);
      } catch (e) {
        setCentres([]);
      }
    };
    fetchCentres();
  }, [isAdmin]);

  const handleFormationAdded = (newFormation) => {
    fetchFormations(currentPage);
  };

  const handleFormationUpdated = () => {
    fetchFormations(currentPage);
  };

  const handleEditClick = (formation) => {
    setFormationToEdit(formation);
    setIsEditDialogOpen(true);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(value);
      setCurrentPage(1);
    }, 3000);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }
  };

  const handleSearchBlur = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (searchTerm !== debouncedSearchTerm) {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }
  };

  const handleFilterChange = (e) => {
    setFilterStatus(e.target.value);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDelete = async (formationId) => {
    console.log('handleDelete called for formation:', formationId);
    console.log('canDelete result:', canDelete('formations'));
    console.log('User permissions:', permissions);
    
    // Utiliser un toast de confirmation au lieu de window.confirm
    const toastId = toast.custom(
      'danger',
      <div style={{ textAlign: 'center' }}>
        <div style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>
          Êtes-vous sûr de vouloir supprimer cette formation ?
        </div>
        <div style={{ marginBottom: '1rem', color: '#6b7280', fontSize: '0.9rem' }}>
          Cette action est irréversible et désactivera la formation.
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1rem' }}>
          <button
            type="button"
            className="toast-btn toast-btn-danger"
            onClick={() => {
              toast.remove(toastId);
              proceedWithDeletion(formationId);
            }}
          >
            Supprimer
          </button>
          <button 
            type="button" 
            className="toast-btn" 
            onClick={() => toast.remove(toastId)}
          >
            Annuler
          </button>
        </div>
      </div>,
      { duration: null }
    );
    
    const proceedWithDeletion = async (formationId) => {
      try {
        console.log('Proceeding with deletion...');
        await formationService.delete(formationId);
        fetchFormations(currentPage);
        toast.success(t('formations.deleteSuccess', { defaultValue: 'Formation supprimée.' }));
      } catch (error) {
        console.error('Error deleting formation:', error);
        toast.error(t('formations.deleteError'));
      }
    };
  };

  const handleHardDelete = async (formationId) => {
    console.log('handleHardDelete called for formation:', formationId);
    
    const toastId = toast.custom(
      'danger',
      <div style={{ textAlign: 'center' }}>
        <div style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600', color: '#dc2626' }}>
          ⚠️ ATTENTION - Suppression définitive
        </div>
        <div style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: '600' }}>
          Êtes-vous sûr de vouloir supprimer DÉFINITIVEMENT cette formation ?
        </div>
        <div style={{ marginBottom: '1rem', color: '#6b7280', fontSize: '0.9rem' }}>
          Cette action est IRRÉVERSIBLE et supprimera complètement la formation de la base de données.
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1rem' }}>
          <button
            type="button"
            className="toast-btn toast-btn-danger"
            onClick={() => {
              toast.remove(toastId);
              proceedWithHardDeletion(formationId);
            }}
          >
            Supprimer définitivement
          </button>
          <button 
            type="button" 
            className="toast-btn" 
            onClick={() => toast.remove(toastId)}
          >
            Annuler
          </button>
        </div>
      </div>,
      { duration: null }
    );
    
    const proceedWithHardDeletion = async (formationId) => {
      try {
        console.log('Proceeding with hard deletion...');
        await formationService.hardDelete(formationId);
        fetchFormations(currentPage);
        toast.success('Formation supprimée définitivement de la base de données.');
      } catch (error) {
        console.error('Error hard deleting formation:', error);
        toast.error('Erreur lors de la suppression définitive de la formation.');
      }
    };
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loader"></div>
        <span>{t('formations.loading')}</span>
      </div>
    );
  }

  return (
    <div className="formations-page">
      <div className="page-header-section">
        <div className="header-titles">
          <h1 className="page-title">
            <BookOpen size={32} />
            {t('formations.title')}
          </h1>
          <p className="page-subtitle">{t('formations.subtitle')}</p>
        </div>
        {canCreate('formations') && (
          <button onClick={() => setIsDialogOpen(true)} className="btn-add">
            <Plus size={20} />
            {t('formations.add')}
          </button>
        )}
      </div>

      <div className="filters-section">
        <div className="search-bar">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder={t('formations.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            onBlur={handleSearchBlur}
            className="search-input"
          />
          {searchTerm && (
            <button 
              className="search-clear-btn"
              onClick={handleClearSearch}
              title={t('common.clearSearch')}
            >
              <X size={16} />
            </button>
          )}
        </div>

        {(isAdmin ? availableCentres.length >= 1 : hasMultipleCentres) && (
          <div className="filter-group">
            <Filter size={18} />
            <select
              value={selectedCentre}
              onChange={(e) => {
                const nextCentre = e.target.value;
                setSelectedCentre(nextCentre);
                setCurrentPage(1);
                fetchFormations(1, nextCentre);
              }}
              className="filter-select"
              disabled={availableCentres.length <= 1}
            >
              <option value="">{allCentresLabel}</option>
              {availableCentres.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="filter-group">
          <Filter size={18} />
          <select
            value={filterStatus}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="all">{t('common.allStatuses')}</option>
            <option value="active">{t('status.active')}</option>
            <option value="inactive">{t('status.inactive')}
            </option>
          </select>
        </div>

        {isAdmin && (
          <div className="filter-group">
            <button
              className={`filter-toggle-btn ${showArchived ? 'active' : ''}`}
              onClick={() => {
                setShowArchived(!showArchived);
                setCurrentPage(1);
              }}
              title={showArchived ? 'Masquer les archives' : 'Afficher les archives'}
            >
              <Archive size={16} />
              <span>{showArchived ? 'Archives' : 'Actives'}</span>
            </button>
          </div>
        )}
      </div>

      <div className="ui-fixed-list">
        {!isMobile && (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('formations.table.title')}</th>
                  <th>{t('formations.table.description')}</th>
                  <th>{t('formations.table.duration')}</th>
                  <th>{t('formations.table.supplies')}</th>
                  <th>{t('formations.table.creator')}</th>
                  <th>{t('formations.table.status')}</th>
                  <th className="actions-column">{t('formations.table.actions')}</th>
                </tr>
              </thead>
            </table>
          </div>
        )}
        <div className="ui-fixed-list-scroll overflow-auto">
          <div className="table-container">
            {isMobile ? (
              <div className="mobile-formations-list">
                <div className="mobile-list-header">
                  <span className="mobile-list-title">{t('formations.table.title')}</span>
                  <span className="mobile-list-actions">{t('formations.table.actions')}</span>
                </div>
                {formations.length === 0 ? (
                  <div className="empty-state">
                    <BookOpen size={48} className="empty-icon" />
                    <h3>{t('formations.empty.title')}</h3>
                    <p>{t('formations.empty.text')}</p>
                  </div>
                ) : (
                  formations.map((formation) => (
                    <div key={formation.id} className="mobile-formation-item">
                      <div className="mobile-formation-name">{formation.title}</div>
                      <div className="mobile-formation-actions">
                        <Link to={`/formations/${formation.id}`} className="action-btn action-view"><Eye size={16} /></Link>
                        {canEdit('formations') && (
                          <button className="action-btn action-edit" onClick={() => handleEditClick(formation)}><Edit size={16} /></button>
                        )}
                        {canDelete('formations') && (
                          <>
                            <button 
                              className="action-btn action-delete" 
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDelete(formation.id);
                              }}
                              title="Supprimer"
                            >
                              <Trash2 size={16} />
                            </button>
                            {showArchived && !formation.is_active && isAdmin && (
                              <button 
                                className="action-btn action-hard-delete" 
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleHardDelete(formation.id);
                                }}
                                title="Supprimer définitivement"
                              >
                                <AlertTriangle size={16} />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <table className="data-table">
                <tbody>
                  {formations.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="empty-state">
                        <BookOpen size={48} className="empty-icon" />
                        <h3>{t('formations.empty.title')}</h3>
                        <p>{t('formations.empty.text')}</p>
                      </td>
                    </tr>
                  ) : (
                    formations.map((formation) => (
                      <tr key={formation.id}>
                        <td className="name-cell">{formation.title}</td>
                        <td className="description-cell">{formation.description}</td>
                        <td className="duration-cell">
                          <Clock size={14} />
                          {t('common.daysCount', { count: formation.duration_days })}
                        </td>
                        <td className="consommables-cell">
                          <Package size={14} />
                          {t('formations.suppliesCount', { count: formation.consommables_count || 0 })}
                        </td>
                        <td>{formation.created_by_name || formation.creator_name || '-'}</td>
                        <td>
                          <span className={`status-badge ${formation.is_active ? 'status-active' : 'status-inactive'}`}>
                            {formation.is_active ? t('status.active') : t('status.inactive')}
                          </span>
                        </td>
                        <td className="actions-cell">
                          <Link to={`/formations/${formation.id}`} className="action-btn action-view"><Eye size={16} /></Link>
                          {canEdit('formations') && (
                            <button className="action-btn action-edit" onClick={() => handleEditClick(formation)}><Edit size={16} /></button>
                          )}
                          {canDelete('formations') && (
                            <>
                              <button 
                                className="action-btn action-delete" 
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDelete(formation.id);
                                }}
                                title="Supprimer"
                              >
                                <Trash2 size={16} />
                              </button>
                              {showArchived && !formation.is_active && isAdmin && (
                                <button 
                                  className="action-btn action-hard-delete" 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleHardDelete(formation.id);
                                  }}
                                  title="Supprimer définitivement"
                                >
                                  <AlertTriangle size={16} />
                                </button>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="table-footer ui-fixed-list-footer">
          <p className="results-count">
            {t('formations.resultsCount', { total: totalCount, currentPage, totalPages })}
          </p>
          <div className="pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              {t('common.previous')}
            </button>
            <div className="page-numbers">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`page-number ${page === currentPage ? 'active' : ''}`}
                >
                  {page}
                </button>
              ))}
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              {t('common.next')}
            </button>
          </div>
        </div>
      </div>
      <AddFormationDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onFormationAdded={handleFormationAdded}
      />
      <EditFormationDialog
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        onFormationUpdated={handleFormationUpdated}
        formationToEdit={formationToEdit}
      />
    </div>
  );
};

export default FormationsPage;
